/**
 * Largura da coluna da lista (feed) na tela de ordens de serviço, em rem — a UI inteira é
 * rem por causa do `html { font-size: 80% }`.
 *
 * Fica fora do componente porque é a parte com regra de verdade: o que o usuário arrasta
 * precisa caber na janela dele, e uma largura lida do storage pode vir de uma tela maior
 * (ou de uma versão anterior destes limites).
 */
export const LIST_WIDTH_DEFAULT_REM = 23.75;
export const LIST_WIDTH_MIN_REM = 17;
export const LIST_WIDTH_MAX_REM = 40;

/** A lista nunca passa disto da janela, mesmo dentro do máximo em rem. */
const LIST_WIDTH_MAX_RATIO = 0.5;

export const LIST_WIDTH_STORAGE_KEY = 'rapidocs:order-list-width-rem';

/**
 * Prende a largura entre o mínimo (abaixo dele o título do documento não cabe) e o menor
 * entre o máximo em rem e metade da janela — numa tela estreita, 40rem deixaria o painel
 * de detalhes menor que a lista.
 *
 * O mínimo vence o máximo quando a janela é minúscula: melhor a lista sobrando um pouco
 * do que uma coluna ilegível.
 */
export function clampListWidthRem(rem: number, remPx: number, windowWidth: number): number {
    const maxByWindow = (windowWidth * LIST_WIDTH_MAX_RATIO) / (remPx || 16);
    const max = Math.max(Math.min(LIST_WIDTH_MAX_REM, maxByWindow), LIST_WIDTH_MIN_REM);

    return Math.min(Math.max(rem, LIST_WIDTH_MIN_REM), max);
}

/** Largura lembrada da sessão anterior, já ajustada à janela de agora. */
export function readStoredListWidthRem(
    storage: Pick<Storage, 'getItem'> | null,
    remPx: number,
    windowWidth: number
): number {
    try {
        const stored = Number(storage?.getItem(LIST_WIDTH_STORAGE_KEY));
        if (!Number.isFinite(stored) || stored <= 0) return LIST_WIDTH_DEFAULT_REM;

        return clampListWidthRem(stored, remPx, windowWidth);
    } catch {
        // Storage bloqueado (janela privada, cookies desligados): a largura vale só para
        // esta sessão, e a tela continua abrindo no padrão.
        return LIST_WIDTH_DEFAULT_REM;
    }
}
